import { useRef, type ChangeEvent } from "react";
import { formatBytes } from "./attachments";
import { useScrollEdges } from "@/lib/scrollEdges";
import { client } from "@/lib/api";
import { useOrgContext } from "@/lib/OrgContext";
import { useAttachmentOpener } from "./attachmentOpener";
import type { AttachmentsController, PendingAttachment } from "./useAttachments";

/** Row of local previews shown above the composer while a message is drafted. */
export function AttachmentTray({ attachments }: { attachments: AttachmentsController }) {
  // Before the early return: a hook cannot be skipped on a render.
  const listRef = useScrollEdges("x");
  const openAttachment = useAttachmentOpener();
  const { orgId } = useOrgContext();
  if (attachments.items.length === 0 && !attachments.error) return null;
  const hasImages = attachments.items.some((item) => item.mediaType.startsWith("image/"));
  // An uploaded file opens from the server so its tab outlives the tray's object URL, and
  // shares its id with the same file once it is sent.
  const open = openAttachment ? (item: PendingAttachment) => openAttachment({
    id: item.ref?.id ?? item.key,
    name: item.file.name || undefined,
    mediaType: item.mediaType,
    size: item.ref?.size ?? item.file.size,
    url: item.ref ? client.attachmentUrl(orgId, item.ref.id) : item.previewUrl,
  }) : undefined;
  return (
    <div className="attachment-tray">
      {attachments.items.length > 0 && (
        // One row that scrolls, not a block that wraps: a ninth attachment used
        // to add a second row and push the whole editor up the page. The fade is
        // the tab strip's, measured per edge.
        <ul ref={listRef} className={`attachment-list${hasImages ? " attachment-list-cards" : " attachment-list-files"}`}>
          {attachments.items.map((item) => (
            <li
              key={item.key}
              className={`attachment-thumb attachment-thumb-${item.status}${hasImages ? " attachment-card" : " attachment-file-pill"}`}
              // A tray restored after a reload holds the server's ref, not the bytes.
              title={item.error ? `${item.file.name} — ${item.error}` : `${item.file.name} · ${formatBytes(item.ref?.size ?? item.file.size)}`}
            >
              {open && (
                <button
                  type="button"
                  className="attachment-thumb-open"
                  aria-label={`Open ${item.file.name || "attachment"}`}
                  onClick={() => open(item)}
                />
              )}
              {hasImages && item.mediaType.startsWith("image/") ? (
                <img src={item.previewUrl} alt={item.file.name || "Attached image"} />
              ) : hasImages ? (
                <span className="attachment-file-preview attachment-file-type" aria-label={item.file.name || "Attached file"}>{fileType(item.file.name)}</span>
              ) : (
                <><span className="attachment-file-type" aria-hidden="true">{fileType(item.file.name)}</span><span className="attachment-file-name">{item.file.name || "Attached file"}</span></>
              )}
              {item.status === "uploading" && (
                <span className="attachment-thumb-veil" aria-label="Uploading">
                  <span className="spinner" />
                </span>
              )}
              {item.status === "error" && (
                <button
                  type="button"
                  className="attachment-thumb-veil attachment-thumb-retry"
                  aria-label={`Retry upload of ${item.file.name || "file"}`}
                  title={item.error}
                  onClick={() => attachments.retry(item.key)}
                >↻</button>
              )}
              <button
                type="button"
                className="attachment-thumb-remove"
                aria-label={`Remove ${item.file.name || "file"}`}
                onClick={() => attachments.remove(item.key)}
              >✕</button>
            </li>
          ))}
        </ul>
      )}
      {attachments.error && (
        <div className="error-text attachment-error" role="alert">
          {attachments.error}
          <button type="button" className="attachment-error-dismiss" aria-label="Dismiss" onClick={attachments.dismissError}>✕</button>
        </div>
      )}
    </div>
  );
}

function fileType(name: string): string {
  const separator = name.lastIndexOf(".");
  return separator > 0 && separator < name.length - 1 ? name.slice(separator + 1).toLowerCase() : "file";
}

/** Neutral add-file control + hidden file input. */
export function AttachButton({
  attachments,
  className = "composer-icon-button",
  disabled = false,
  label = "Attach files",
  showLabel = false,
}: {
  attachments: AttachmentsController;
  className?: string;
  disabled?: boolean;
  label?: string;
  showLabel?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    attachments.addFiles(Array.from(event.target.files ?? []));
    event.target.value = "";
  }

  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={label}
        title={label}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <PlusIcon />
        {showLabel && <span>Files</span>}
      </button>
      <input
        ref={inputRef}
        type="file"
        className="visually-hidden"
        multiple
        tabIndex={-1}
        aria-hidden="true"
        onChange={onChange}
      />
    </>
  );
}

function PlusIcon() {
  return (
    // Sized and weighted like the runtime icons beside it (see RuntimeControls).
    <svg className="runtime-icon" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 1.8v8.4M1.8 6h8.4" />
    </svg>
  );
}
